import type AiCostFacilityOptionView from '@UI/Store/AiCosts/Views/AiCostFacilityOptionView';
import type AiCostOrganizationOptionView from '@UI/Store/AiCosts/Views/AiCostOrganizationOptionView';
import type AiCostUserOptionView from '@UI/Store/AiCosts/Views/AiCostUserOptionView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * The structures, facilities and users of the caller's scope, with the cascade between them.
 */
class AiCostFilterOptionsView {
	readonly accountId: number;
	readonly organizations: readonly AiCostOrganizationOptionView[];
	readonly facilities: readonly AiCostFacilityOptionView[];
	readonly users: readonly AiCostUserOptionView[];

	constructor(
		accountId: number,
		organizations: readonly AiCostOrganizationOptionView[],
		facilities: readonly AiCostFacilityOptionView[],
		users: readonly AiCostUserOptionView[],
	) {
		this.accountId = accountId;
		this.organizations = organizations;
		this.facilities = facilities;
		this.users = users;

		Object.freeze(this);
	}

	/** The facilities of a structure, or all of them when no structure is chosen. */
	facilitiesOf(organizationId: number | null): readonly AiCostFacilityOptionView[] {
		if (organizationId === null) {
			return this.facilities;
		}

		return this.facilities.filter((facility) => facility.organizationId === organizationId);
	}

	/** The users of a facility, else of a structure's facilities, else all of them. */
	usersOf(organizationId: number | null, facilityId: number | null): readonly AiCostUserOptionView[] {
		if (facilityId !== null) {
			return this.users.filter((user) => user.facilityIds.includes(facilityId));
		}

		if (organizationId === null) {
			return this.users;
		}

		const facilityIds = this.facilitiesOf(organizationId).map((facility) => facility.id);

		return this.users.filter((user) => user.facilityIds.some((id) => facilityIds.includes(id)));
	}
}

export default AiCostFilterOptionsView;
